/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    // Accent colors for metric cards and UI variety
    accents?: {
      green: string;      // For revenue/growth metrics
      blue: string;       // For rates/percentages
      purple: string;     // For unique metrics
      teal: string;       // For alternative metrics
    };
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "SalesGrid",
    tagline: "Sales Dashboard",
    copyright: "SalesGrid",
  },

  logos: {
    primary: "/logo.png",
    wordmark: "/logo.png",
    favicon: "/favicon.ico",
  },

  colors: {
    // Main brand colors - Vibrant Orange theme (#F04911)
    primary: "#F04911",      // Vibrant orange - primary actions, active states
    secondary: "#F76035",    // Light orange - gradient start
    tertiary: "#C93B0C",     // Dark burnt orange - gradient end
    accent: "#F89A7A",       // Soft coral - highlights, badges
    ring: "#F04911",         // Vibrant orange - focus rings

    // Gradient definition
    gradient: {
      start: "#F76035",      // Light orange
      end: "#C93B0C",        // Dark burnt orange
      angle: 135,            // Gradient angle (degrees)
    },

    // Chart color palette - Vibrant Orange-focused for brand consistency
    chart: [
      "#F04911",  // Vibrant orange (primary brand color)
      "#F76035",  // Light orange
      "#C93B0C",  // Dark burnt orange
      "#D8501E",  // Rich orange
      "#A83008",  // Deep rust
      "#E86A38",  // Warm orange
      "#8F2706",  // Dark rust
      "#F47D50",  // Bright coral
    ],

    // Accent colors for metric cards and UI variety (complement the orange brand)
    accents: {
      green: "#10b981",   // Emerald green - for revenue/growth
      blue: "#0ea5e9",    // Sky blue - for rates/percentages
      purple: "#8b5cf6",  // Violet - for unique metrics
      teal: "#14b8a6",    // Teal - for alternative metrics
    },

    // Page background colors
    background: {
      light: "#f5f5f5",   // Light gray for light mode
      dark: "#0a0a0a",    // Near black for dark mode
    },

    // Card/panel background colors
    card: {
      light: "#ffffff",   // White for light mode
      dark: "#171717",    // Dark gray for dark mode
    },
  },

  email: {
    headerGradientStart: "#F76035",
    headerGradientEnd: "#C93B0C",
    buttonBackground: "#F04911",
    accentText: "#F04911",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider (backward-compatible wrapper)
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors
  colors.chart.forEach((color, i) => {
    root.style.setProperty(`--chart-${i + 1}`, color);
  });

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Skip setting --background and --card here — injectThemeCSS() from the
  // preset system is the source of truth for these. Setting them here would
  // overwrite the preset's semantic colors (e.g. #FFF7EE → #F5F5F5).
}

/**
 * Inject full theme CSS variables from a ThemePreset.
 * Sets brand colors, semantic vars, typography, card styling, and component vars.
 */
export function injectThemeCSS(
  preset: import("./theme-presets").ThemePreset,
  isDark: boolean
): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors, semantic, typography, cardStyle, components, scrollbar } = preset;
  const mode = isDark ? "dark" : "light";
  const sem = semantic[mode];
  const isSalesGridDark = preset.id === "salesgrid" && isDark && !root.classList.contains("orange");

  // ── Brand colors ──
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors
  colors.chart.forEach((color, i) => {
    root.style.setProperty(`--chart-${i + 1}`, color);
  });

  // Background / card brand vars
  root.style.setProperty("--brand-bg-light", colors.background.light);
  root.style.setProperty("--brand-bg-dark", colors.background.dark);
  root.style.setProperty("--brand-card-light", colors.card.light);
  root.style.setProperty("--brand-card-dark", colors.card.dark);

  // ── Semantic color variables ──
  const isOrangeTheme = root.classList.contains("orange");
  // Orange theme needs transparent --background so the body gradient shows through
  root.style.setProperty("--background", isOrangeTheme ? "transparent" : sem.background);
  root.style.setProperty("--foreground", isOrangeTheme ? "#ffffff" : sem.foreground);
  root.style.setProperty("--card", isOrangeTheme ? "rgba(80, 25, 5, 0.55)" : sem.card);

  // Update body background color to match the active preset (CSS var drives the body bg)
  if (!isOrangeTheme) {
    root.style.setProperty("--body-bg-color", sem.background);
    root.style.setProperty("--body-bg-image", isSalesGridDark ? "none" : "");
    root.style.setProperty("--body-bg-size", isSalesGridDark ? "auto" : "");
    root.style.setProperty("--body-bg-attachment", isSalesGridDark ? "scroll" : "");
  }
  root.style.setProperty("--card-foreground", isOrangeTheme ? "#ffffff" : sem.cardForeground);
  root.style.setProperty("--popover", sem.popover);
  root.style.setProperty("--popover-foreground", sem.popoverForeground);

  // Orange theme overrides for translucent UI
  if (isOrangeTheme) {
    root.style.setProperty("--secondary", "rgba(255, 255, 255, 0.12)");
    root.style.setProperty("--secondary-foreground", "#ffffff");
    root.style.setProperty("--muted", "rgba(255, 255, 255, 0.1)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.7)");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.15)");
    root.style.setProperty("--input", "rgba(255, 255, 255, 0.15)");
    root.style.setProperty("--sidebar", "transparent");
    root.style.setProperty("--sidebar-foreground", "#ffffff");
  }
  root.style.setProperty("--primary", sem.primary);
  root.style.setProperty("--primary-foreground", sem.primaryForeground);
  root.style.setProperty("--secondary", sem.secondary);
  root.style.setProperty("--secondary-foreground", sem.secondaryForeground);
  root.style.setProperty("--muted", sem.muted);
  root.style.setProperty("--muted-foreground", sem.mutedForeground);
  root.style.setProperty("--accent", sem.accent);
  root.style.setProperty("--accent-foreground", sem.accentForeground);
  root.style.setProperty("--destructive", sem.destructive);
  root.style.setProperty("--border", sem.border);
  root.style.setProperty("--input", sem.input);
  root.style.setProperty("--ring", sem.ring);
  root.style.setProperty("--sidebar", sem.sidebar);
  root.style.setProperty("--sidebar-foreground", sem.sidebarForeground);
  root.style.setProperty("--sidebar-primary", sem.sidebarPrimary);
  root.style.setProperty("--sidebar-primary-foreground", sem.sidebarPrimaryForeground);
  root.style.setProperty("--sidebar-accent", sem.sidebarAccent);
  root.style.setProperty("--sidebar-accent-foreground", sem.sidebarAccentForeground);
  root.style.setProperty("--sidebar-border", sem.sidebarBorder);
  root.style.setProperty("--sidebar-ring", sem.sidebarRing);

  // ── Typography ──
  root.style.setProperty("--theme-font-family", preset.fontFamily);
  root.style.setProperty("--theme-page-title-size", typography.pageTitle.fontSize);
  root.style.setProperty("--theme-page-title-weight", typography.pageTitle.fontWeight);
  root.style.setProperty("--theme-page-title-tracking", typography.pageTitle.letterSpacing ?? "normal");
  root.style.setProperty("--theme-section-title-size", typography.sectionTitle.fontSize);
  root.style.setProperty("--theme-section-title-weight", typography.sectionTitle.fontWeight);
  root.style.setProperty("--theme-card-title-size", typography.cardTitle.fontSize);
  root.style.setProperty("--theme-card-title-weight", typography.cardTitle.fontWeight);
  root.style.setProperty("--theme-kpi-value-size", typography.kpiValue.fontSize);
  root.style.setProperty("--theme-kpi-value-weight", typography.kpiValue.fontWeight);
  root.style.setProperty("--theme-body-size", typography.body.fontSize);
  root.style.setProperty("--theme-body-weight", typography.body.fontWeight);
  root.style.setProperty("--theme-small-size", typography.small.fontSize);

  // ── Card styling ──
  root.style.setProperty("--theme-card-radius", cardStyle.borderRadius);
  root.style.setProperty("--theme-card-padding", cardStyle.padding);
  root.style.setProperty("--theme-card-shadow", isDark ? cardStyle.shadow.dark : cardStyle.shadow.light);
  root.style.setProperty("--theme-card-backdrop", cardStyle.backdropBlur);

  // ── Components ──
  root.style.setProperty("--theme-button-radius", components.buttonRadius);
  root.style.setProperty("--theme-input-radius", components.inputRadius);
  root.style.setProperty("--radius", components.baseRadius);

  // ── Layout ──
  const { layout } = preset;
  root.style.setProperty("--theme-icon-box-size", layout.iconBoxSize);
  root.style.setProperty("--theme-icon-box-radius", layout.iconBoxRadius);
  root.style.setProperty("--theme-icon-size", layout.iconSize);
  root.style.setProperty("--theme-kpi-grid-min", layout.kpiGridMin);
  root.style.setProperty("--theme-grid-gap", layout.gridGap);
  root.style.setProperty("--theme-content-padding", layout.contentPadding);
  root.style.setProperty("--theme-label-size", layout.labelSize);

  // ── Scrollbar ──
  root.style.setProperty("--theme-scrollbar-width", scrollbar.width);
  root.style.setProperty("--theme-scrollbar-thumb", scrollbar.thumbColor);

  // ── Font loading ──
  const existingFontLink = document.getElementById("theme-font") as HTMLLinkElement | null;
  if (preset.fontUrl) {
    if (preset.fontUrl.endsWith(".woff2")) {
      // Direct font file — handled by @font-face in CSS, no link needed
      if (existingFontLink) existingFontLink.remove();
    } else {
      // Google Fonts stylesheet URL
      if (existingFontLink) {
        existingFontLink.href = preset.fontUrl;
      } else {
        const link = document.createElement("link");
        link.id = "theme-font";
        link.rel = "stylesheet";
        link.href = preset.fontUrl;
        document.head.appendChild(link);
      }
    }
  } else if (existingFontLink) {
    existingFontLink.remove();
  }

  // Apply font family directly
  document.body.style.fontFamily = preset.fontFamily;
  root.style.fontFamily = preset.fontFamily;

  // ── Clear any stale inline body background styles (CSS custom properties handle it now) ──
  document.body.style.backgroundColor = "";
  document.body.style.backgroundImage = "";
  document.body.style.backgroundSize = "";
  document.body.style.backgroundAttachment = "";

  // ── Glassmorphism toggle ──
  if (cardStyle.backdropBlur !== "none") {
    root.classList.add("theme-glassmorphism");
  } else {
    root.classList.remove("theme-glassmorphism");
  }
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
